import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../app/store';
import { useDeletePolicyMutation } from '../../../../services/CaseManagementService';

const DeletePolicyButton: React.FC = () => {
  const navigate = useNavigate();
  const policy = useAppSelector((state) => state.policy.currentPolicy);
  const selectedCompany = useAppSelector((state) => state.policy.selectedCompany);
  const [deletePolicyMutation, { isLoading }] = useDeletePolicyMutation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleDeleteClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmDialogOpen(false);

    if (!policy?.id || !selectedCompany) {
      setNotification({
        open: true,
        message: 'Unable to delete policy: missing policy ID or company information',
        severity: 'error',
      });
      return;
    }

    try {
      await deletePolicyMutation({
        company: selectedCompany,
        policyId: policy.id,
      }).unwrap();

      setNotification({
        open: true,
        message: 'Policy deleted successfully!',
        severity: 'success',
      });

      // Navigate back to policies list after successful delete
      setTimeout(() => {
        const queryParam = selectedCompany ? `?company=${encodeURIComponent(selectedCompany)}` : '';
        navigate(`/case-management/policies${queryParam}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error deleting policy:', error);
      setNotification({
        open: true,
        message: error?.data?.message || 'Failed to delete policy',
        severity: 'error',
      });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Don't show delete button if policy doesn't have an ID (new policy)
  if (!policy?.id) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleDeleteClick}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
        sx={{
          borderColor: '#dc2626',
          color: '#dc2626',
          fontWeight: 600,
          px: 3,
          '&:hover': {
            backgroundColor: '#fef2f2',
            borderColor: '#b91c1c',
            color: '#b91c1c',
          },
          '&:disabled': {
            borderColor: '#94a3b8',
            color: '#94a3b8',
          },
        }}
      >
        {isLoading ? 'Deleting...' : 'Delete Policy'}
      </Button>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-policy-dialog-title"
        aria-describedby="delete-policy-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle 
          id="delete-policy-dialog-title"
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: '#dc2626',
            fontWeight: 600,
          }}
        >
          <WarningIcon />
          Confirm Policy Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-policy-dialog-description">
            You are about to delete the policy "{policy?.policyName || 'Unknown Policy'}" 
            (Policy Number: {policy?.policyNumber || 'N/A'}). 
            This action is <strong>irreversible</strong> and cannot be undone.
            <br /><br />
            Are you sure you want to permanently delete this policy?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{
              borderColor: '#6b7280',
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f9fafb',
                borderColor: '#374151',
                color: '#374151',
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#dc2626',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#b91c1c',
              },
            }}
          >
            Delete Policy
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'success' ? 4000 : 6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeletePolicyButton;